import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Delete } from '@factorialco/f0-react/icons/app'
import { AnimatePresence, motion } from 'framer-motion'

import { observer } from 'mobx-react'

import DirectUploader from 'modules/trainings/components/Files/Uploaders/DirectUploader'
import 'modules/trainings/components/content/index.styles.css'
import 'modules/trainings/components/content/items/shared/index.styles.css'
import { FADE_ANIMATION } from 'modules/trainings/components/content/items/shared/sharedAnimations'
import { AttachedFile } from 'modules/trainings/lib/content/AttachedFile'

export type ContentThumbnailProps = {
  thumbnail: AttachedFile
  readOnly: boolean
  trainingId: string
  description: string
  title?: string
  onChange: (thumbnail: AttachedFile) => void
  onDelete: () => void
}

const CONTENT_MAX_WIDTH_STYLE = { width: '100%', maxWidth: '1080px' }

type AnimatedWrapperProps = {
  children: React.ReactNode
  animationKey: string
}

const AnimatedWrapper = ({ children, animationKey }: AnimatedWrapperProps) => (
  <motion.div key={animationKey} style={CONTENT_MAX_WIDTH_STYLE} {...FADE_ANIMATION}>
    {children}
  </motion.div>
)

type ThumbnailDisplayProps = {
  thumbnailUrl: string
  title?: string
  readOnly: boolean
  onDelete: () => void
}

const ThumbnailDisplay = ({ thumbnailUrl, title, readOnly, onDelete }: ThumbnailDisplayProps) => (
  <Box width="full" style={{ position: 'relative' }} className={'content_imageContainer'}>
    <img src={thumbnailUrl} alt={title || ''} className={'content_image'} />
    {!readOnly && (
      <Box className={'shared_mediaOverlay'} background="grey400">
        <F0Button variant="neutral" size="md" onClick={onDelete} icon={Delete} hideLabel />
      </Box>
    )}
  </Box>
)

type ThumbnailUploaderProps = {
  trainingId: string
  description: string
  onChange: (thumbnail: AttachedFile) => void
  onDelete: () => void
}

const ThumbnailUploader = ({
  trainingId,
  description,
  onChange,
  onDelete,
}: ThumbnailUploaderProps) => {
  const handleUpload = async (uploadData: { file: File; signedId?: string }) => {
    const newThumbnail = AttachedFile.fromUpload(uploadData.file, uploadData.signedId || '')
    onChange?.(newThumbnail)
  }

  return (
    <DirectUploader
      onUpload={handleUpload}
      onDelete={onDelete}
      description={description}
      fileTypes={['image']}
      resourceType="Trainings::Training"
      resourceId={trainingId}
    />
  )
}

export const ContentThumbnail = observer(
  ({
    thumbnail,
    readOnly = true,
    trainingId,
    description,
    title,
    onChange,
    onDelete,
  }: ContentThumbnailProps) => {
    const hasThumbnail = !!thumbnail.url
    const shouldShowUploader = !hasThumbnail && !readOnly && trainingId

    return (
      <Box width="full" alignItems="center">
        <AnimatePresence mode="wait">
          {hasThumbnail && (
            <AnimatedWrapper animationKey="'styles_image'">
              <ThumbnailDisplay
                thumbnailUrl={thumbnail.url}
                title={title}
                readOnly={readOnly}
                onDelete={onDelete}
              />
            </AnimatedWrapper>
          )}

          {shouldShowUploader && (
            <AnimatedWrapper animationKey="uploader">
              <ThumbnailUploader
                trainingId={trainingId}
                description={description || ''}
                onChange={onChange}
                onDelete={onDelete}
              />
            </AnimatedWrapper>
          )}
        </AnimatePresence>
      </Box>
    )
  }
)
